import { asyncComputeRealValue } from "../compute/computeRealValue.js";
import { getProviderBrick } from "./getProviderBrick.js";
import { _internalApiGetRenderId, hooks } from "../Runtime.js";
import { markAsComputed } from "../compute/markAsComputed.js";
import { get } from "lodash";
import { isStrictMode, warnAboutStrictMode } from "../../isStrictMode.js";
const cache = new Map();
export async function resolveData(resolveConf, runtimeContext, resolveOptions) {
  var _hooks$flowApi;
  const {
    useProvider,
    method = "resolve",
    args,
    params,
    options,
    filename,
    onReject
  } = resolveConf;
  const {
    provider: legacyProvider,
    field: legacyField
  } = resolveConf;
  if (legacyProvider && !useProvider) {
    throw new Error(`You're using "provider: ${legacyProvider}" which is dropped in v3, please use "useProvider" instead`);
  }
  const hasLegacyField = legacyField !== null && legacyField !== undefined;
  if (hasLegacyField) {
    const strict = isStrictMode();
    warnAboutStrictMode(strict, "`resolve.field`");
    // istanbul ignore next
    if (strict) {
      throw new Error("Using deprecated `resolve.field`");
    }
  }
  const contractConf = !Array.isArray(args) && params && hooks !== null && hooks !== void 0 && (_hooks$flowApi = hooks.flowApi) !== null && _hooks$flowApi !== void 0 && _hooks$flowApi.isFlowApiProvider(useProvider) ? {
    params,
    options,
    filename
  } : null;
  const [providerBrick, actualArgs, actualContractConf] = await Promise.all([getProviderBrick(useProvider), asyncComputeRealValue(args, runtimeContext), asyncComputeRealValue(contractConf, runtimeContext)]);

  // `clearResolveCache` maybe called during the above promise being
  // fulfilled, so we manually mark it as stale for this case.
  const renderId = resolveOptions === null || resolveOptions === void 0 ? void 0 : resolveOptions.renderId;
  const isStale = () => !!renderId && renderId !== _internalApiGetRenderId();
  const promise = resolveByProvider(providerBrick, useProvider, method, actualContractConf !== null && actualContractConf !== void 0 && actualContractConf.params ? actualContractConf : actualArgs !== null && actualArgs !== void 0 ? actualArgs : [], resolveOptions, args, isStale);
  let {
    transform
  } = resolveConf;
  let data;
  try {
    const value = await promise;
    data = hasLegacyField ? get(value, legacyField) : value;
    // The fetched data and its inner objects should never be *injected* again.
    markAsComputed(data);
  } catch (error) {
    if (isHandleRejectByTransform(onReject)) {
      transform = onReject.transform;
      data = error;
    } else {
      throw error;
    }
  }
  if (!transform) {
    return data;
  }

  // Keep backward compatibility of string transform.
  if (typeof transform === "string") {
    return {
      [transform]: data
    };
  }
  return asyncComputeRealValue(transform, {
    ...runtimeContext,
    data
  });
}
export function clearResolveCache() {
  cache.clear();
}
export async function resolveByProvider(brick, useProvider, method, args, options, originalArgs, isStale) {
  let cacheKey;
  try {
    // `args` may contain circular references, which makes
    // JSON stringify failed, thus we fallback to original args.
    cacheKey = JSON.stringify({
      useProvider,
      method,
      args
    });
  } catch (e) {
    if (!originalArgs) {
      throw e;
    }
    cacheKey = JSON.stringify({
      useProvider,
      method,
      originalArgs
    });
  }
  let promise;
  if ((options === null || options === void 0 ? void 0 : options.cache) !== "reload") {
    promise = cache.get(cacheKey);
  }
  if (!promise) {
    promise = (async _hooks$flowApi2 => {
      const finalArgs = hooks !== null && hooks !== void 0 && (_hooks$flowApi2 = hooks.flowApi) !== null && _hooks$flowApi2 !== void 0 && _hooks$flowApi2.isFlowApiProvider(useProvider) ? await hooks.flowApi.getArgsOfFlowApi(useProvider, args, method) : args;
      return brick[method](...finalArgs);
    })();
    if (!(isStale !== null && isStale !== void 0 && isStale())) {
      cache.set(cacheKey, promise);
    }
  }
  return promise;
}
function isHandleRejectByTransform(onReject) {
  return !!(onReject !== null && onReject !== void 0 && onReject.transform);
}
//# sourceMappingURL=resolveData.js.map